import axios from 'axios';
import { SERVER_NAME } from '../config';

function getErrorMessage(error, fallback) {
  try {
    const status = error && error.response && error.response.status ? Number(error.response.status) : 0;
    const message = error && error.response && error.response.data && error.response.data.message
      ? String(error.response.data.message)
      : error && error.message
        ? String(error.message)
        : '';
    if (message) return status ? `${message} (${status})` : message;
  } catch (e) {
  }
  return fallback;
}

async function getJson(url, params, options = {}) {
  const response = await axios.get(url, {
    params,
    headers: options.headers || {},
    withCredentials: options.withCredentials !== false,
  });
  return response && response.data ? response.data : null;
}

async function postJson(url, data, options = {}) {
  const response = await axios.post(url, data, {
    headers: options.headers || {},
    withCredentials: options.withCredentials !== false,
  });
  return response && response.data ? response.data : null;
}

async function putJson(url, data, options = {}) {
  const response = await axios.put(url, data, {
    headers: options.headers || {},
    withCredentials: options.withCredentials !== false,
  });
  return response && response.data ? response.data : null;
}

export async function fetchTokenMetadata(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/metadata`, {
      chain: options.chain || 'ETH',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token metadata'));
  }
}

export async function fetchTokenPriceHistory(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/price-history`, {
      range: options.range || '24h',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token price history'));
  }
}

export async function fetchTokenLiquidity(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/liquidity`, {
      range: options.range || '24h',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token liquidity'));
  }
}

export async function fetchTokenStories(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/stories`, {});
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token stories'));
  }
}

export async function fetchTokenTheses(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/theses`, {});
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token theses'));
  }
}

export async function fetchTokenNarrativeFlow(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/tokens/${address}/narrative-flow`, {
      range: options.range || '24h',
      minUsd: options.minUsd != null ? options.minUsd : 5000,
      chain: options.chain || 'ETH',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load narrative flow'));
  }
}

// Combined bundle endpoint — returns metadata, priceHistory, liquidity, stories, theses in one request.
// narrativeFlow is excluded (pass includeNarrativeFlow: true to include it) and fetched separately
// via fetchTokenNarrativeFlow to avoid blocking the fast data on the slow flow query.
export async function fetchTokenBundle(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/token/${address}`, {
      chain: options.chain || 'ETH',
      range: options.range || '24h',
      minUsd: options.minUsd != null ? options.minUsd : 5000,
      includeNarrativeFlow: 'false',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load token data'));
  }
}

export async function fetchPaymentProducts() {
  try {
    return await getJson(`${SERVER_NAME}/api/payments/products`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load payment products'));
  }
}

export async function createProjectClaim(payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/project-claims`, payload, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to start project claim'));
  }
}

export async function createProjectClaimNonce(claimId, payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/project-claims/${claimId}/nonces`, payload, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create verification nonce'));
  }
}

export async function verifyProjectClaimWallet(claimId, payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/project-claims/${claimId}/verify/wallet`, payload, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to verify project wallet'));
  }
}

export async function fetchAdminProjectClaimReview(claimId) {
  try {
    return await getJson(`${SERVER_NAME}/api/admin/project-claims/${claimId}`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load claim review'));
  }
}

export async function reviewAdminProjectClaim(claimId, payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/admin/project-claims/${claimId}/review`, payload);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to review project claim'));
  }
}

export async function fetchAdminProjectDiscoveryCandidates(params = {}) {
  try {
    return await getJson(`${SERVER_NAME}/api/admin/project-discovery/candidates`, params);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load project discovery candidates'));
  }
}

export async function fetchAdminProjectDiscoveryEvidence(candidateId) {
  try {
    return await getJson(`${SERVER_NAME}/api/admin/project-discovery/candidates/${encodeURIComponent(candidateId)}/evidence`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load project discovery evidence'));
  }
}

export async function approveAdminProjectDiscoveryContact(candidateId, payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/admin/project-discovery/candidates/${encodeURIComponent(candidateId)}/approve-contact`, payload);
  } catch (error) {
    const status = error && error.response && error.response.status ? Number(error.response.status) : 0;
    // This route only exists when the server has PROJECT_OUTREACH_ROUTES_ENABLED
    // set; a 404 here almost always means that flag is off, not a missing
    // candidate (candidateId is already validated by the page before this
    // call). Say so instead of surfacing a generic network error.
    if (status === 404) {
      throw new Error('Outreach review is not enabled on this server (PROJECT_OUTREACH_ROUTES_ENABLED is off).');
    }
    throw new Error(getErrorMessage(error, 'Failed to approve outreach contact'));
  }
}

export async function fetchVerifiedProject(address, options = {}) {
  try {
    return await getJson(`${SERVER_NAME}/api/verified-projects/${address}`, {
      chain: options.chain || 'ethereum',
      canonicalProjectId: options.canonicalProjectId || '',
    }, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load verified project'));
  }
}

export async function createEntitlementChallenge(payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/entitlements/challenge`, payload, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create entitlement challenge'));
  }
}

export async function verifyEntitlementChallenge(payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/entitlements/verify`, payload, { withCredentials: false });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to verify entitlement challenge'));
  }
}

export async function claimToken(address, payload) {
  try {
    return await postJson(`${SERVER_NAME}/api/registry/tokens/${address}/claim`, payload, {
      withCredentials: false,
      headers: payload && payload.sessionToken ? { Authorization: `Bearer ${payload.sessionToken}` } : {},
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to claim token'));
  }
}

export async function fetchMyTokenClaims(payload = {}) {
  try {
    return await getJson(`${SERVER_NAME}/api/registry/my-claims`, {
      chain: payload.chain || '',
      wallet: payload.wallet || '',
    }, {
      headers: payload && payload.sessionToken ? { Authorization: `Bearer ${payload.sessionToken}` } : {},
      withCredentials: false,
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load claimed tokens'));
  }
}

export async function updateClaim(address, payload, apiKey) {
  try {
    return await putJson(`${SERVER_NAME}/api/registry/tokens/${address}/claim`, payload, {
      withCredentials: false,
      headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update token claim'));
  }
}
